import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;
  #debugLastPayload = '';
  #stickyTopTolerancePx = 1;
  #lastChromeSampleAt = 0;

  #debugLog = (hypothesisId, location, message, data = {}) => {
    const payload = {
      sessionId: 'a816d8',
      runId: 'chrome-debug-1',
      hypothesisId,
      location,
      message,
      data,
      timestamp: Date.now(),
    };
    const payloadKey = `${hypothesisId}:${message}:${JSON.stringify(data)}`;
    if (this.#debugLastPayload === payloadKey) return;
    this.#debugLastPayload = payloadKey;

    // #region agent log
    fetch('http://127.0.0.1:7512/ingest/a5c8484a-1227-4846-8700-b390b001eb92',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'a816d8'},body:JSON.stringify(payload)}).catch(()=>{});
    // #endregion

    // #region agent log
    console.debug('[a816d8][header-debug]', payload);
    // #endregion
  };

  #debugChromeSnapshot = (trigger, scrollTop) => {
    const now = Date.now();
    if (now - this.#lastChromeSampleAt < 250) return;
    this.#lastChromeSampleAt = now;

    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const appleStatusMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    const colorSchemeMeta = document.querySelector('meta[name="color-scheme"]');

    const headerSection = this.closest('.header-section');
    const headerClosedUnderlay = this.querySelector('.header__underlay-closed');
    const htmlStyle = getComputedStyle(document.documentElement);
    const bodyStyle = getComputedStyle(document.body);
    const headerStyle = getComputedStyle(this);
    const sectionStyle = headerSection ? getComputedStyle(headerSection) : null;
    const underlayStyle = headerClosedUnderlay ? getComputedStyle(headerClosedUnderlay) : null;

    this.#debugLog('H6', 'assets/header.js:debugChromeSnapshot', 'top chrome paint snapshot', {
      trigger,
      scrollTop,
      headerTop: this.getBoundingClientRect().top,
      headerSectionTop: headerSection?.getBoundingClientRect().top ?? null,
      visualViewportOffsetTop: window.visualViewport?.offsetTop ?? null,
      visualViewportHeight: window.visualViewport?.height ?? null,
      viewportMeta: viewportMeta?.getAttribute('content') ?? null,
      themeColorMeta: themeColorMeta?.getAttribute('content') ?? null,
      appleStatusMeta: appleStatusMeta?.getAttribute('content') ?? null,
      colorSchemeMeta: colorSchemeMeta?.getAttribute('content') ?? null,
      headerTransparentAttr: this.getAttribute('transparent') ?? null,
      stickyAttr: this.getAttribute('sticky') ?? null,
      stickyState: this.dataset.stickyState ?? null,
      htmlBg: htmlStyle.backgroundColor,
      bodyBg: bodyStyle.backgroundColor,
      headerBg: headerStyle.backgroundColor,
      headerSectionBg: sectionStyle?.backgroundColor ?? null,
      underlayDisplay: underlayStyle?.display ?? null,
      underlayHeight: underlayStyle?.height ?? null,
      underlayBg: underlayStyle?.backgroundImage ?? null,
    });
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }

      this.#debugLog('H2', 'assets/header.js:intersectionObserver', 'intersection state updated', {
        stickyMode: this.getAttribute('sticky'),
        alwaysSticky,
        isIntersecting,
        stickyState: this.dataset.stickyState || null,
        offscreen: this.#offscreen,
      });
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const headerSectionTop = this.closest('.header-section')?.getBoundingClientRect().top ?? null;
    const visualViewportOffsetTop = window.visualViewport?.offsetTop ?? null;
    const visualViewportHeight = window.visualViewport?.height ?? null;
    const visualViewportPageTop = window.visualViewport?.pageTop ?? null;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= -this.#stickyTopTolerancePx;

    this.#debugLog('H1', 'assets/header.js:updateScrollState:entry', 'scroll tick', {
      stickyMode,
      offscreen: this.#offscreen,
      stickyState: this.dataset.stickyState || null,
      scrollDirection: this.dataset.scrollDirection || null,
      scrollTop,
      lastScrollTop: this.#lastScrollTop,
      headerTop,
      headerSectionTop,
      visualViewportOffsetTop,
      visualViewportHeight,
      visualViewportPageTop,
      stickyTopTolerancePx: this.#stickyTopTolerancePx,
      isAtTop,
      isScrollingUp,
    });

    if (scrollTop < 240 || Math.abs(headerTop) < 4) {
      this.#debugChromeSnapshot('scroll', scrollTop);
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
        this.#debugLog('H5', 'assets/header.js:updateScrollState:always:down', 'downward scroll branch', {
          headerTop,
          headerSectionTop,
          visualViewportOffsetTop,
          visualViewportHeight,
          visualViewportPageTop,
          scrollTop,
          lastScrollTop: this.#lastScrollTop,
          stickyState: this.dataset.stickyState || null,
        });
      }

      this.#debugLog('H3', 'assets/header.js:updateScrollState:always', 'always sticky direction applied', {
        headerTop,
        isAtTop,
        isScrollingUp,
        appliedScrollDirection: this.dataset.scrollDirection || null,
      });

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
    this.#debugLog('H4', 'assets/header.js:updateScrollState:scroll-up-mode', 'scroll-up mode state applied', {
      stickyState: this.dataset.stickyState || null,
      scrollDirection: this.dataset.scrollDirection || null,
      offscreen: this.#offscreen,
      headerTop,
      isAtTop,
      isScrollingUp,
    });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#debugChromeSnapshot('connected', document.scrollingElement?.scrollTop ?? 0);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
